import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useLanguage } from "../contexts/LanguageContext";
import LanguageSwitcher from "./LanguageSwitcher";
import { UserBadge } from "./UserBadges";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Package, Link2, Globe, CreditCard, Wallet, TrendingUp,
  HeadphonesIcon, Key, LogOut, Menu, X, Send,
} from "lucide-react";

const RootSeoLogo = ({ size = "default" }) => {
  const { getLogo } = useTheme();
  const logo = getLogo();
  const sizeClasses = { small: "w-8 h-8", default: "w-10 h-10" };
  return (
    <div className={`${sizeClasses[size]} relative`}>
      <div className="absolute inset-0 bg-gradient-to-br from-primary to-secondary rounded-full" />
      <div
        className="relative w-full h-full flex items-center justify-center text-black p-1.5 rounded-full overflow-hidden"
        dangerouslySetInnerHTML={{ __html: logo.svg }}
      />
    </div>
  );
};

const CustomerLayout = ({ activeId, children, mainClassName = "" }) => {
  const { user, setUser, logout } = useAuth();
  const { language } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ oldPassword: "", newPassword: "", confirmPassword: "" });
  const [changingPassword, setChangingPassword] = useState(false);
  const [telegramDialogOpen, setTelegramDialogOpen] = useState(false);
  const [telegramUsername, setTelegramUsername] = useState("");
  const [savingTelegram, setSavingTelegram] = useState(false);

  const navItems = [
    { id: "dashboard", icon: Package, label: language === "tr" ? "GENEL BAKIŞ" : "DASHBOARD", path: "/dashboard" },
    { id: "mylinks", icon: Link2, label: language === "tr" ? "LİNKLERİM" : "MY LINKS", path: "/my-links" },
    { id: "sites", icon: Globe, label: language === "tr" ? "SİTELER" : "SITES", path: "/sites" },
    { id: "subscription", icon: CreditCard, label: language === "tr" ? "ABONELİK" : "SUBSCRIPTION", path: "/credits" },
    { id: "seo", icon: TrendingUp, label: language === "tr" ? "SEO HİZMETLERİ" : "SEO SERVICES", path: "/seo-services" },
    { id: "support", icon: HeadphonesIcon, label: language === "tr" ? "DESTEK" : "SUPPORT", path: "/support" },
    { id: "api-balance", icon: Wallet, label: language === "tr" ? "API BAKİYE" : "API BALANCE", path: "/api-balance" },
    { id: "api", icon: Key, label: "API", path: "/api-access" },
  ];

  const navClass = (id) =>
    `flex items-center gap-3 px-4 py-3 mb-1 font-mono text-sm transition-all ${
      id === activeId
        ? "bg-primary/10 text-primary border-l-2 border-primary"
        : "text-muted-foreground hover:bg-white/5 hover:text-white"
    }`;

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error(language === "tr" ? "Şifreler eşleşmiyor" : "Passwords do not match");
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      toast.error(language === "tr" ? "Şifre en az 6 karakter olmalı" : "Password must be at least 6 characters");
      return;
    }
    setChangingPassword(true);
    try {
      await api.post("/auth/change-password", {
        old_password: passwordForm.oldPassword,
        new_password: passwordForm.newPassword,
      });
      toast.success(language === "tr" ? "Şifre başarıyla değiştirildi" : "Password changed successfully");
      setPasswordDialogOpen(false);
      setPasswordForm({ oldPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "tr" ? "Şifre değiştirilemedi" : "Failed to change password"));
    } finally {
      setChangingPassword(false);
    }
  };

  const normalizeTelegramInput = (value) => value.trim().replace(/^@+/, "");

  const openTelegramDialog = () => {
    setTelegramUsername(normalizeTelegramInput(user?.telegram_username || ""));
    setTelegramDialogOpen(true);
  };

  const handleTelegramSave = async (e) => {
    e.preventDefault();
    const cleanUsername = normalizeTelegramInput(telegramUsername);
    if (!/^[A-Za-z0-9_]{5,32}$/.test(cleanUsername)) {
      toast.error(language === "tr" ? "Telegram kullanıcı adı 5-32 karakter olmalı" : "Telegram username must be 5-32 characters");
      return;
    }
    setSavingTelegram(true);
    try {
      const response = await api.put("/auth/telegram", { telegram_username: cleanUsername });
      setUser(response.data);
      setTelegramDialogOpen(false);
      toast.success(language === "tr" ? "Telegram hesabı güncellendi" : "Telegram account updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "tr" ? "Telegram hesabı kaydedilemedi" : "Could not save Telegram account"));
    } finally {
      setSavingTelegram(false);
    }
  };

  const apiBal = Number(user?.api_balance ?? user?.credits ?? 0);

  const subscriptionActive = Boolean(
    user?.is_subscribed &&
    user?.subscription_end_date &&
    new Date(user.subscription_end_date.replace("Z", "+00:00")) > new Date()
  );
  let subscriptionEndLabel = "";
  let subscriptionDaysLeft = 0;
  if (subscriptionActive) {
    const end = new Date(user.subscription_end_date.replace("Z", "+00:00"));
    subscriptionEndLabel = end.toLocaleDateString(language === "tr" ? "tr-TR" : "en-US");
    subscriptionDaysLeft = Math.max(0, Math.ceil((end.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
  }

  const subscriptionInfo = subscriptionActive ? (
    <div className="mt-2 space-y-0.5 font-mono text-[10px] leading-snug">
      <p className="text-emerald-400 font-bold uppercase tracking-wide">
        {language === "tr" ? "Abonelik Aktif" : "Subscription Active"}
      </p>
      <p className="text-muted-foreground">
        {language === "tr" ? "Bitiş: " : "Ends: "}
        <span className="text-foreground/90">{subscriptionEndLabel}</span>
      </p>
      <p className="text-muted-foreground">
        {language === "tr" ? "Kalan: " : "Left: "}
        <span className="text-primary font-bold">
          {subscriptionDaysLeft} {language === "tr" ? "gün" : "days"}
        </span>
      </p>
    </div>
  ) : null;

  const accountActions = (
    <div className="space-y-1">
      <div className="flex items-center gap-2 mb-1">
        <LanguageSwitcher className="flex-shrink-0" />
        <Link
          to="/api-balance"
          title={language === "tr" ? "API Bakiye" : "API Balance"}
          className={`flex-1 min-w-0 flex items-center justify-center gap-1.5 px-2 py-1.5 border font-mono text-xs transition-all ${
            activeId === "api-balance"
              ? "border-primary/50 bg-primary/10 text-primary"
              : "border-white/10 bg-white/5 text-primary hover:border-primary/50 hover:bg-white/10"
          }`}
        >
          <Wallet className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="font-bold truncate">${apiBal.toFixed(0)}</span>
        </Link>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setPasswordDialogOpen(true)}
        className="w-full justify-start gap-2 text-muted-foreground hover:text-primary font-mono text-xs h-8"
      >
        <Key className="w-3 h-3" />
        {language === "tr" ? "Şifre Değiştir" : "Change Password"}
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={openTelegramDialog}
        className="w-full justify-start gap-2 text-muted-foreground hover:text-primary font-mono text-xs h-8"
      >
        <Send className="w-3 h-3" />
        {language === "tr" ? "Telegram Hesabı" : "Telegram Account"}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={logout}
        className="w-full justify-start gap-2 text-muted-foreground hover:text-red-400 font-mono text-xs h-8"
      >
        <LogOut className="w-3 h-3" />
        {language === "tr" ? "Çıkış Yap" : "Logout"}
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen">
      <aside className="fixed left-0 top-0 h-full w-60 border-r border-white/10 bg-black hidden md:flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center gap-2 mb-8">
              <RootSeoLogo />
              <div className="sparkle-wrapper">
                <span className="sparkle"></span>
                <span className="sparkle"></span>
                <span className="sparkle"></span>
                <span className="sparkle"></span>
                <span className="glow-credit font-mono font-bold tracking-normal">ROOT-SEO</span>
              </div>
            </div>
          </div>
          <nav className="px-4">
            {navItems.map((item) => (
              <Link key={item.id} to={item.path} className={navClass(item.id)}>
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
        {/* Altta sabit; biraz daha yukari uzayan hesap alani */}
        <div className="flex-shrink-0 px-4 pt-5 pb-4 border-t border-white/10 bg-black">
          <div className="mb-3">
            <p className="font-mono text-xs text-muted-foreground">{language === "tr" ? "Hoşgeldin," : "Welcome,"}</p>
            <p className="font-mono font-bold text-sm truncate">{user?.name}</p>
            {user?.badges?.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {user.badges.map((badge, idx) => (
                  <UserBadge key={idx} badge={badge} language={language} size="small" />
                ))}
              </div>
            )}
            {subscriptionInfo}
          </div>
          {accountActions}
        </div>
      </aside>

      <header className="md:hidden fixed top-0 left-0 right-0 h-16 border-b border-white/10 bg-black/90 backdrop-blur z-40 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <RootSeoLogo size="small" />
          <span className="font-mono font-bold">ROOT-SEO</span>
        </div>
        <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </header>

      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 bg-black/95 z-30 flex flex-col">
          <div className="flex-1 overflow-y-auto pt-16 px-4 pb-6">
            <nav className="space-y-1 mb-4">
              {navItems.map((item) => (
                <Link
                  key={item.id}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-4 font-mono rounded ${
                    item.id === activeId ? "bg-white/5 text-white" : "text-muted-foreground"
                  }`}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {item.label}
                </Link>
              ))}
            </nav>
            <div className="border-t border-white/10 pt-3 px-2">
              <div className="mb-3">
                <p className="font-mono text-xs text-muted-foreground">{language === "tr" ? "Hoşgeldin," : "Welcome,"}</p>
                <p className="font-mono font-bold text-sm truncate">{user?.name}</p>
                {user?.badges?.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {user.badges.map((badge, idx) => (
                      <UserBadge key={idx} badge={badge} language={language} size="small" />
                    ))}
                  </div>
                )}
                {subscriptionInfo}
              </div>
              {accountActions}
            </div>
          </div>
        </div>
      )}

      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="glass border-white/10">
          <DialogHeader>
            <DialogTitle className="font-mono">{language === "tr" ? "Şifre Değiştir" : "Change Password"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <Label className="font-mono text-sm">{language === "tr" ? "Mevcut Şifre" : "Current Password"}</Label>
              <Input type="password" value={passwordForm.oldPassword} onChange={(e) => setPasswordForm((p) => ({ ...p, oldPassword: e.target.value }))} className="font-mono" required />
            </div>
            <div>
              <Label className="font-mono text-sm">{language === "tr" ? "Yeni Şifre" : "New Password"}</Label>
              <Input type="password" value={passwordForm.newPassword} onChange={(e) => setPasswordForm((p) => ({ ...p, newPassword: e.target.value }))} className="font-mono" required minLength={6} />
            </div>
            <div>
              <Label className="font-mono text-sm">{language === "tr" ? "Şifre Tekrar" : "Confirm Password"}</Label>
              <Input type="password" value={passwordForm.confirmPassword} onChange={(e) => setPasswordForm((p) => ({ ...p, confirmPassword: e.target.value }))} className="font-mono" required minLength={6} />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setPasswordDialogOpen(false)} className="flex-1 font-mono">
                {language === "tr" ? "İptal" : "Cancel"}
              </Button>
              <Button type="submit" disabled={changingPassword} className="flex-1 font-mono bg-primary text-black">
                {changingPassword
                  ? (language === "tr" ? "Değiştiriliyor..." : "Changing...")
                  : (language === "tr" ? "Değiştir" : "Change")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={telegramDialogOpen} onOpenChange={setTelegramDialogOpen}>
        <DialogContent className="glass border-white/10">
          <DialogHeader>
            <DialogTitle className="font-mono">{language === "tr" ? "Telegram Hesabı" : "Telegram Account"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleTelegramSave} className="space-y-4">
            <div>
              <Label className="font-mono text-sm">{language === "tr" ? "Telegram kullanıcı adı" : "Telegram username"}</Label>
              <div className="flex mt-2">
                <span className="inline-flex items-center border border-r-0 border-white/10 bg-black/50 px-3 font-mono text-muted-foreground">@</span>
                <Input
                  value={telegramUsername}
                  onChange={(e) => setTelegramUsername(normalizeTelegramInput(e.target.value))}
                  placeholder="username"
                  className="bg-black/50 border-white/10 rounded-none font-mono"
                  required
                />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setTelegramDialogOpen(false)} className="flex-1 font-mono">
                {language === "tr" ? "İptal" : "Cancel"}
              </Button>
              <Button type="submit" disabled={savingTelegram} className="flex-1 font-mono bg-primary text-black">
                {savingTelegram
                  ? (language === "tr" ? "Kaydediliyor..." : "Saving...")
                  : (language === "tr" ? "Kaydet" : "Save")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <main className={`md:ml-60 pt-16 md:pt-0 min-h-screen ${mainClassName}`}>
        {children}
      </main>
    </div>
  );
};

export default CustomerLayout;
